"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
// Removed RichTextEditor - using raw HTML textarea instead
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Send, Save, Eye } from "lucide-react";
import { updateEmailTemplateSchema } from "@/lib/email/validation";
import { EmailTemplate } from "@/types/email";
import { EMAIL_TRIGGER_TYPES } from "@/types/email";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { useAuth } from "@/contexts/auth-and-permissions-context";
import { ROLES, type RoleId } from "@/lib/constants/roles";

// Create a schema without the id field requirement for the form
const emailFormSchema = updateEmailTemplateSchema.omit({ id: true });

type EmailFormData = z.infer<typeof emailFormSchema>;

interface EmailTemplateEditClientProps {
  venue: any;
  show: any;
  emailTemplate: EmailTemplate;
  availableLanguages: string[];
  venueId: number;
  showId: number | null;  // null for road show templates
  userRoleId?: RoleId;
  isRoadShow?: boolean;
}

// Template variables based on trigger type
const getTemplateVariables = (triggerType: string) => {
  const baseVariables = {
    "CUSTOMER INFORMATION": [
      {
        variable: "{{customer_name}}",
        description: "Full name of the customer",
      },
      {
        variable: "{{customer_email}}",
        description: "Email address of the customer",
      },
      {
        variable: "{{customer_phone}}",
        description: "Phone number of the customer",
      },
      {
        variable: "{{company_name}}",
        description: "Company name (if applicable)",
      },
    ],
    "BOOKING DETAILS": [
      {
        variable: "{{booking_number}}",
        description: "Unique booking reference number",
      },
      { variable: "{{booking_date}}", description: "Date of the booking" },
      { variable: "{{booking_time}}", description: "Time of the booking" },
      { variable: "{{participants}}", description: "Number of participants" },
    ],
    "SHOW & VENUE INFORMATION": [
      { variable: "{{show_names}}", description: "Names of booked shows" },
      { variable: "{{show_duration}}", description: "Duration of the show" },
      { variable: "{{venue_name}}", description: "Name of the venue" },
      {
        variable: "{{venue_address}}",
        description: "Full address of the venue",
      },
      {
        variable: "{{venue_phone}}",
        description: "Venue contact phone number",
      },
      { variable: "{{venue_email}}", description: "Venue contact email" },
    ],
    FINANCIAL: [
      { variable: "{{total_price}}", description: "Total booking amount" },
      { variable: "{{min_charge}}", description: "Minimum charge amount" },
      {
        variable: "{{currency}}",
        description: "Currency code (e.g., USD, EUR)",
      },
    ],
    "SYSTEM LINKS": [
      { variable: "{{portal_link}}", description: "Link to customer portal" },
      { variable: "{{cancel_link}}", description: "Direct cancellation link" },
      {
        variable: "{{survey_link}}",
        description:
          "Link to this booking's survey (pre-filled to the correct booking)",
      },
    ],
    ACTIVITY: [
      {
        variable: "{{latest_activity}}",
        description: "Description of the most recent change to the booking",
      },
    ],
  };

  // Add specific variables based on trigger type
  if (triggerType === "post_show") {
    baseVariables["SYSTEM LINKS"].push({
      variable: "{{review_link}}",
      description: "Link to leave a review",
    });
  }

  if (triggerType === "booking_cancellation") {
    baseVariables["FINANCIAL"].push({
      variable: "{{refund_amount}}",
      description: "Amount to be refunded",
    });
  }

  return baseVariables;
};

// Get friendly name for trigger type
const getTriggerTypeName = (triggerType: string) => {
  const names: { [key: string]: string } = {
    booking_confirmation: "Booking Confirmation",
    payment_received: "Payment Received",
    reminder_24h: "24 Hour Reminder",
    reminder_2h: "2 Hour Reminder",
    post_show: "Post Show",
    booking_cancellation: "Booking Cancellation",
    booking_modification: "Booking Modification",
  };
  return names[triggerType] || triggerType;
};

export function EmailTemplateEditClient({
  venue,
  show,
  emailTemplate,
  availableLanguages,
  venueId,
  showId,
  userRoleId = ROLES.BOOKING_STAFF,
  isRoadShow = false,
}: EmailTemplateEditClientProps) {
  const router = useRouter();
  const { user } = useAuth();

  // Determine the portal path based on user role
  const portalPath =
    userRoleId === ROLES.SUPER_ADMIN ? "/superadmin" : "/booking";
  const [isLoading, setIsLoading] = useState(false);
  const [isTestSending, setIsTestSending] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    availableLanguages[0] || "en",
  );
  const [languageContent, setLanguageContent] = useState<{
    [key: string]: { subject: string; body: string };
  }>({});
  const [templateVariables, setTemplateVariables] = useState<
    Record<
      string,
      Array<{ variable: string; description: string; example?: string }>
    >
  >({});
  const hasFetchedVariables = useRef(false);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const lastCursorPosition = useRef<number | null>(null);

  // Parse the initial content to get the default language values
  const getInitialContent = (field: 'subject' | 'body', defaultLang: string) => {
    const content = emailTemplate[field];
    if (!content) return "";

    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (typeof parsed === 'object' && parsed[defaultLang]) {
          return parsed[defaultLang];
        }
      } catch {
        // If not JSON, return as is for the first language
        return availableLanguages[0] === defaultLang ? content : "";
      }
    }
    return "";
  };

  const defaultLanguage = availableLanguages[0] || "en";

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      from_name: emailTemplate.from_name || venue.name || "",
      reply_to: venue.email || emailTemplate.reply_to || "", // Always use venue email
      cc_emails: (emailTemplate as any).cc_emails || "",
      bcc_emails: (emailTemplate as any).bcc_emails || "",
      subject: getInitialContent('subject', defaultLanguage),
      body: getInitialContent('body', defaultLanguage),
      status_id: emailTemplate.status_id,
    } as any,
  });

  // Fetch template variables from API
  useEffect(() => {
    if (hasFetchedVariables.current) return;

    const fetchTemplateVariables = async () => {
      hasFetchedVariables.current = true;

      try {
        const response = await fetch("/api/email-template-variables");
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            let variables = result.data;

            // Filter out add-on variables if not editing the addon_special_questions template
            if (emailTemplate.trigger_type !== "addon_special_questions") {
              // Create a filtered version of the variables
              const filteredVariables: typeof variables = {};

              for (const [category, vars] of Object.entries(variables)) {
                if (Array.isArray(vars)) {
                  // Filter out addon_name and special_addon_instructions
                  // but keep add_on_list which is available for all templates
                  const filteredVars = vars.filter((v: any) => {
                    const varName = v.variable;
                    return varName !== "{{addon_name}}" &&
                           varName !== "{{special_addon_instructions}}";
                  });

                  // Only add the category if it has remaining variables
                  if (filteredVars.length > 0) {
                    filteredVariables[category] = filteredVars;
                  }
                }
              }

              variables = filteredVariables;
            }

            setTemplateVariables(variables);
          }
        }
      } catch (error) {
        console.error("Failed to fetch template variables:", error);
        // Fall back to hardcoded variables if API fails
        setTemplateVariables(getTemplateVariables(emailTemplate.trigger_type));
      }
    };

    fetchTemplateVariables();
  }, [emailTemplate.trigger_type]);

  // Initialize language content from email template
  useEffect(() => {
    const initialContent: { [key: string]: { subject: string; body: string } } =
      {};

    console.log("Email template raw data:", {
      subject: emailTemplate.subject,
      body: emailTemplate.body?.substring(0, 200) + "...", // Log first 200 chars
    });

    // Check if the data is already parsed (might come from props already as object)
    let parsedSubject: any = emailTemplate.subject;
    let parsedBody: any = emailTemplate.body;

    // If the data is a string, try to parse it as JSON
    if (typeof emailTemplate.subject === 'string') {
      try {
        parsedSubject = JSON.parse(emailTemplate.subject);
      } catch {
        // If parsing fails, treat it as a plain string for the first language
        parsedSubject = { [availableLanguages[0]]: emailTemplate.subject };
      }
    }

    if (typeof emailTemplate.body === 'string') {
      try {
        parsedBody = JSON.parse(emailTemplate.body);
      } catch {
        // If parsing fails, treat it as a plain string for the first language
        parsedBody = { [availableLanguages[0]]: emailTemplate.body };
      }
    }

    // Now extract content for each language
    availableLanguages.forEach((lang) => {
      let langContent = { subject: "", body: "" };

      // Get subject for this language
      if (parsedSubject && typeof parsedSubject === 'object') {
        langContent.subject = parsedSubject[lang] || "";
      }

      // Get body for this language
      if (parsedBody && typeof parsedBody === 'object') {
        langContent.body = parsedBody[lang] || "";
      }

      console.log(`Content for ${lang}:`, {
        subject: langContent.subject.substring(0, 50),
        body: langContent.body.substring(0, 100),
      });

      initialContent[lang] = langContent;
    });

    console.log("Final language content:", initialContent);
    setLanguageContent(initialContent);
  }, [emailTemplate, availableLanguages]);

  // Update form values when language content or current language changes
  useEffect(() => {
    if (languageContent[currentLanguage]) {
      form.setValue("subject", languageContent[currentLanguage].subject);
      form.setValue("body", languageContent[currentLanguage].body);
    }
  }, [languageContent, currentLanguage, form]);

  // Insert variable into the current textarea content at cursor position
  const insertVariable = (variable: string) => {
    const textarea = bodyTextareaRef.current;
    const currentValue = form.getValues("body") || "";

    // Use the last known cursor position, or current position if textarea is focused
    let insertPosition = currentValue.length; // Default to end

    if (textarea) {
      if (document.activeElement === textarea) {
        // If textarea is focused, use current cursor position
        insertPosition = textarea.selectionStart;
      } else if (lastCursorPosition.current !== null) {
        // Use last known cursor position
        insertPosition = Math.min(lastCursorPosition.current, currentValue.length);
      }
    }

    // Insert variable at the determined position
    const newValue =
      currentValue.substring(0, insertPosition) +
      variable +
      currentValue.substring(insertPosition);

    form.setValue("body", newValue);
    updateCurrentLanguageContent("body", newValue);

    // Update cursor position for next insert
    const newPosition = insertPosition + variable.length;
    lastCursorPosition.current = newPosition;

    // Restore focus and set cursor position after inserted variable
    setTimeout(() => {
      if (bodyTextareaRef.current) {
        bodyTextareaRef.current.focus();
        bodyTextareaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleLanguageChange = (lang: string) => {
    // Save current form data to language content before switching
    const currentSubject = form.getValues("subject");
    const currentBody = form.getValues("body");

    const updatedContent = {
      ...languageContent,
      [currentLanguage]: {
        subject: currentSubject || "",
        body: currentBody || "",
      },
    };

    setLanguageContent(updatedContent);

    // Switch to new language
    setCurrentLanguage(lang);

    // Form values will be updated by the useEffect that watches languageContent and currentLanguage
  };

  const updateCurrentLanguageContent = (
    field: "subject" | "body",
    value: string,
  ) => {
    setLanguageContent((prev) => ({
      ...prev,
      [currentLanguage]: {
        ...prev[currentLanguage],
        [field]: value,
      },
    }));
  };

  const handlePreview = () => {
    const formData = form.getValues();

    // Always use local preview since preview API endpoint doesn't exist
    setPreviewData({
      from_name: formData.from_name || venue.name,
      reply_to: venue.email || formData.reply_to, // Always use venue email
      subject: formData.subject || "Email Subject",
      body: formData.body || "Email content will appear here...",
    });
    setShowPreview(true);
    setIsLoading(false);
  };

  // Generate preview on mount and when form values change
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const subscription = form.watch(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handlePreview();
      }, 500); // Wait 500ms after user stops typing
    });

    // Initial preview
    handlePreview();

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [venueId, showId]);

  const handleSendTest = async () => {
    console.log("📧 [TEST EMAIL] User from context:", {
      hasUser: !!user,
      userEmail: user?.email,
    });

    console.log(
      "📧 [TEST EMAIL] Sending test email - email will be fetched from API auth",
    );

    setIsTestSending(true);

    try {
      // Get current form data
      const formData = form.getValues();

      // Create test email data with current template content
      // Note: 'to' field removed - API will get email from Supabase auth
      const testEmailData = {
        from_name: formData.from_name || venue.name || "OnAir",
        reply_to: venue.email || "noreply@onair.com", // Always use venue email
        subject: formData.subject || "Test Email Subject",
        body: formData.body || "Test email content",
        trigger_type: emailTemplate.trigger_type,
        language: currentLanguage,
      };

      const apiUrl = isRoadShow
        ? `/api/venues/${venueId}/road-show/email-templates/${emailTemplate.trigger_type.replace(/_/g, "-")}`
        : `/api/venues/${venueId}/shows/${showId}/email-templates/${emailTemplate.trigger_type.replace(/_/g, "-")}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testEmailData),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      const result = await response.json();

      if (result.success) {
        toast.success("Test email sent successfully to your email address!");
      } else {
        throw new Error(result.error || "Failed to send test email");
      }
    } catch (error) {
      console.error("Test email error:", error);
      toast.error(
        `Failed to send test email: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setIsTestSending(false);
    }
  };

  const onSubmit = async (data: EmailFormData) => {
    console.log("onSubmit called with data:", data);
    setIsLoading(true);

    // Save current form data to language content
    const currentSubject = form.getValues("subject");
    const currentBody = form.getValues("body");

    const finalLanguageContent = {
      ...languageContent,
      [currentLanguage]: {
        subject: currentSubject,
        body: currentBody,
      },
    };

    // Prepare multi-language data
    const subjects: { [key: string]: string } = {};
    const bodies: { [key: string]: string } = {};

    availableLanguages.forEach((lang) => {
      subjects[lang] = finalLanguageContent[lang]?.subject || "";
      bodies[lang] = finalLanguageContent[lang]?.body || "";
    });

    try {
      const apiUrl = isRoadShow
        ? `/api/venues/${venueId}/road-show/email-templates/${emailTemplate.trigger_type}`
        : `/api/venues/${venueId}/shows/${showId}/email-templates/${emailTemplate.trigger_type}`;

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: JSON.stringify(subjects), // Store as JSON for multi-language
          body: JSON.stringify(bodies), // Store as JSON for multi-language
          fromName: data.from_name,
          replyTo: venue.email || data.reply_to, // Always use venue email
          ccEmails: (data as any).cc_emails || null,
          bccEmails: (data as any).bcc_emails || null,
          statusId: data.status_id,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      toast.success("Email template saved successfully!");
      const redirectUrl = isRoadShow
        ? `${portalPath}/venue/${venueId}/road-show`
        : `${portalPath}/venue/${venueId}/shows/${showId}?tab=emails`;
      router.push(redirectUrl);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save email template");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClick = () => {
    console.log("Save button clicked");
    console.log("Form errors:", form.formState.errors);
    console.log("Form values:", form.getValues());
    form.handleSubmit(onSubmit)();
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const redirectUrl = isRoadShow
                ? `${portalPath}/venue/${venueId}/road-show`
                : `${portalPath}/venue/${venueId}/shows/${showId}?tab=emails`;
              router.push(redirectUrl);
            }}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {getTriggerTypeName(emailTemplate.trigger_type)} Email
            </h1>
            <p className="text-muted-foreground">
              {venue.name} → {isRoadShow ? "Road show" : show.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <FormField
            control={form.control}
            name="status_id"
            render={({ field }) => (
              <Select
                value={field.value?.toString()}
                onValueChange={(value) => field.onChange(parseInt(value))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={STATUS_IDS.EMAIL_TEMPLATE_DRAFT.toString()}
                  >
                    Draft
                  </SelectItem>
                  <SelectItem
                    value={STATUS_IDS.EMAIL_TEMPLATE_ACTIVE.toString()}
                  >
                    Active
                  </SelectItem>
                  <SelectItem
                    value={STATUS_IDS.EMAIL_TEMPLATE_ARCHIVED.toString()}
                  >
                    Archived
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />

          <Button
            variant="outline"
            onClick={handleSendTest}
            disabled={isTestSending}
          >
            <Send className="h-4 w-4 mr-2" />
            Send Test Email
          </Button>

          <Button
            variant="outline"
            onClick={() => {
              const redirectUrl = isRoadShow
                ? `${portalPath}/venue/${venueId}/road-show`
                : `${portalPath}/venue/${venueId}/shows/${showId}?tab=emails`;
              router.push(redirectUrl);
            }}
          >
            Cancel
          </Button>

          <Button onClick={handleSaveClick} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save & Exit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor Section */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>EDITOR</CardTitle>
              <CardDescription>
                Customize the{" "}
                {getTriggerTypeName(emailTemplate.trigger_type).toLowerCase()}{" "}
                email template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="from_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            placeholder="Your Venue Name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cc_emails"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CC Emails (Visible to Customer)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            placeholder="email1@example.com, email2@example.com"
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground mt-1">
                          These emails will be visible to customers in the CC field.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="bcc_emails"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>BCC Emails (Hidden from Customer)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            placeholder="manager@example.com, staff@example.com"
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground mt-1">
                          <strong>Recommended:</strong> Use BCC for staff notifications. These emails are completely hidden from customers and prevent direct contact to staff.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Reply-to field hidden - always uses venue email */}
                  <input type="hidden" {...form.register("reply_to")} value={venue.email || ""} />

                  {/* Multi-language Subject and Body */}
                  <div className="space-y-4">
                    <Tabs
                      value={currentLanguage}
                      onValueChange={handleLanguageChange}
                    >
                      <TabsList className="mb-4">
                        {availableLanguages.map((lang) => (
                          <TabsTrigger key={lang} value={lang}>
                            {lang.toUpperCase()}
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      {availableLanguages.map((lang) => (
                        <TabsContent key={lang} value={lang}>
                          {/* Only render content for the active tab */}
                          {currentLanguage === lang && (
                            <div className="space-y-4" data-language={lang}>
                              <FormField
                                control={form.control}
                                name="subject"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Subject ({lang.toUpperCase()})
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="Email subject line"
                                        onChange={(e) => {
                                          field.onChange(e);
                                          updateCurrentLanguageContent(
                                            "subject",
                                            e.target.value,
                                          );
                                        }}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="body"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Message Body ({lang.toUpperCase()}) - HTML
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        {...field}
                                        ref={(e) => {
                                          // Handle both refs - React Hook Form's and our own
                                          field.ref(e);
                                          if (currentLanguage === lang) {
                                            bodyTextareaRef.current = e;
                                          }
                                        }}
                                        onChange={(e) => {
                                          field.onChange(e);
                                          updateCurrentLanguageContent(
                                            "body",
                                            e.target.value,
                                          );
                                        }}
                                        onSelect={(e) => {
                                          // Track cursor position when user selects text or moves cursor
                                          if (bodyTextareaRef.current && e.currentTarget === bodyTextareaRef.current) {
                                            lastCursorPosition.current = e.currentTarget.selectionStart;
                                          }
                                        }}
                                        onClick={(e) => {
                                          // Track cursor position on click
                                          if (bodyTextareaRef.current && e.currentTarget === bodyTextareaRef.current) {
                                            lastCursorPosition.current = e.currentTarget.selectionStart;
                                          }
                                        }}
                                        placeholder="Enter HTML content here. Example:&#10;&#10;&lt;h1&gt;Welcome!&lt;/h1&gt;&#10;&lt;p&gt;Hello {{customer_name}},&lt;/p&gt;&#10;&lt;p&gt;Your booking for &lt;strong&gt;{{show_names}}&lt;/strong&gt; is confirmed.&lt;/p&gt;&#10;&lt;ul&gt;&#10;  &lt;li&gt;Date: {{booking_date}}&lt;/li&gt;&#10;  &lt;li&gt;Time: {{booking_time}}&lt;/li&gt;&#10;  &lt;li&gt;Venue: {{venue_name}}&lt;/li&gt;&#10;&lt;/ul&gt;"
                                        className="font-mono text-sm min-h-[450px] resize-y"
                                        spellCheck={false}
                                      />
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Write raw HTML. Basic tags supported: p, h1-h6, strong, em, ul, ol, li, a, br. Click variables below to insert at cursor position.
                                    </p>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <div className="space-y-6">
          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>PREVIEW</CardTitle>
              <CardDescription>
                See how your email will look with sample data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {previewData ? (
                <div className="space-y-3">
                  <div className="text-sm">
                    <strong>From:</strong> {previewData.from_name}
                  </div>
                  <div className="text-sm">
                    <strong>Reply To:</strong> {venue.email}
                  </div>
                  <div className="text-sm">
                    <strong>Subject:</strong> {previewData.subject}
                  </div>
                  <div className="border-t pt-3">
                    <div className="bg-white p-4 rounded-lg min-h-[450px] overflow-y-auto">
                      <div
                        className="email-preview-content"
                        dangerouslySetInnerHTML={{
                          __html: `
                            <style>
                              .email-preview-content h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
                              .email-preview-content h2 { font-size: 1.5em; font-weight: bold; margin: 0.83em 0; }
                              .email-preview-content h3 { font-size: 1.17em; font-weight: bold; margin: 1em 0; }
                              .email-preview-content h4 { font-size: 1em; font-weight: bold; margin: 1.33em 0; }
                              .email-preview-content h5 { font-size: 0.83em; font-weight: bold; margin: 1.67em 0; }
                              .email-preview-content h6 { font-size: 0.67em; font-weight: bold; margin: 2.33em 0; }
                              .email-preview-content p { margin: 1em 0; }
                              .email-preview-content ul { list-style-type: disc; padding-left: 40px; margin: 1em 0; }
                              .email-preview-content ol { list-style-type: decimal; padding-left: 40px; margin: 1em 0; }
                              .email-preview-content li { display: list-item; }
                              .email-preview-content a { color: #0000EE; text-decoration: underline; }
                              .email-preview-content hr { border: 1px solid #ccc; margin: 1em 0; }
                              .email-preview-content table { border-collapse: collapse; }
                              .email-preview-content strong { font-weight: bold; }
                              .email-preview-content em { font-style: italic; }
                              .email-preview-content br { display: block; margin: 0.5em 0; content: ""; }
                            </style>
                            ${previewData.body}
                          `
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground">
                    Loading preview...
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Template Variables - Full Width at Bottom */}
      <Card>
        <CardHeader>
          <CardTitle>Available Template Variables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {Object.entries(templateVariables).map(([category, variables]) => (
              <div key={category}>
                <h4 className="font-medium text-sm mb-2">{category}</h4>
                <div className="space-y-2">
                  {variables.map((item) => (
                    <div
                      key={item.variable}
                      className="cursor-pointer group"
                      onMouseDown={(e) => {
                        // Prevent default to stop focus loss from editor
                        e.preventDefault();
                        insertVariable(item.variable);
                      }}
                    >
                      <div className="text-xs font-mono text-foreground/70 group-hover:text-foreground transition-colors">
                        {item.variable}
                      </div>
                      <div className="text-xs text-muted-foreground/60">
                        {item.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
